/**
 * Leviathan Web - Widget Picker
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Panel for toggling dashboard widgets on/off
 */

import { useDashboardStore } from '@/stores/dashboardStore';
import { X } from 'lucide-react';

const WIDGET_INFO: Record<string, { name: string; description: string }> = {
  'todays-dispatches': {
    name: "Today's Dispatches",
    description: 'Count of dispatches scheduled today with upcoming list',
  },
  'monthly-revenue': {
    name: 'Monthly Revenue',
    description: 'Current month revenue with MoM and YoY comparisons',
  },
  'active-deliveries': {
    name: 'Active Deliveries',
    description: 'Count of currently active (en route / delivering) dispatches',
  },
  'past-due-invoices': {
    name: 'Past Due Invoices',
    description: 'Overdue invoice count and total amount owed',
  },
  'low-tank-alerts': {
    name: 'Low Tank Alerts',
    description: 'Storage tanks below 30% utilization threshold',
  },
  'completed-today': {
    name: 'Completed Today',
    description: 'Dispatches completed today with completion rate',
  },
  'quick-actions': {
    name: 'Quick Actions',
    description: 'Shortcut buttons for creating orders, dispatches, invoices, and receipts',
  },
  'recent-activity': {
    name: 'Recent Activity',
    description: 'Latest orders, invoices, and dispatches with tabbed view',
  },
};

interface WidgetPickerProps {
  onClose: () => void;
}

const WidgetPicker = ({ onClose }: WidgetPickerProps) => {
  const { widgets, toggleWidget } = useDashboardStore();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="relative w-full max-w-lg mx-4">
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-panel-border">
            <h2 className="text-sm font-display text-steel uppercase tracking-wide">
              Widget Configuration
            </h2>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-amber transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Widget list */}
          <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
            {widgets.map((widget) => {
              const info = WIDGET_INFO[widget.id];
              if (!info) return null;

              return (
                <div
                  key={widget.id}
                  className="flex items-center justify-between p-3 bg-background/30 border border-panel-border rounded hover:border-amber/30 transition-colors"
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="text-sm font-mono text-text-primary">{info.name}</div>
                    <div className="text-[10px] text-text-tertiary mt-0.5">{info.description}</div>
                  </div>

                  {/* Toggle switch */}
                  <button
                    onClick={() => toggleWidget(widget.id)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      widget.visible ? 'bg-amber' : 'bg-panel-border'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        widget.visible ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-panel-border">
            <button
              onClick={onClose}
              className="w-full py-2 px-4 bg-amber/10 hover:bg-amber/20 border border-amber/30 text-amber text-xs font-mono uppercase tracking-wider transition-colors rounded"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetPicker;
